import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
  Alert,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ShoppingCart as ShoppingCartIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PointOfSale as PointOfSaleIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";
import { can } from "../../utils/access";
import TerminalOrderDialog, { TerminalPriceList } from "./TerminalOrderDialog";

function PaymentTerminalCard() {
  const { t } = useTranslation();
  const { currentOrganization, fetchUser } = useUserStore();
  const organizationId = currentOrganization?.organization_id;
  const canOrder = can(currentOrganization, "settings.general");
  const [terminals, setTerminals] = useState([]);
  const [organization, setOrganization] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTerminal, setEditingTerminal] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTerminal, setDeletingTerminal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(null);
  const [clearing, setClearing] = useState(null);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearingTerminal, setClearingTerminal] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Form state
  const [formData, setFormData] = useState({
    terminal_id: "",
    name: "",
    api_username: "",
    api_password: "",
  });

  useEffect(() => {
    fetchTerminals();
  }, []);

  // Org settings: whether a terminal order was already sent, and the order form prefill.
  useEffect(() => {
    if (!organizationId) return;
    axios
      .get(`/api/organizations/${organizationId}/`)
      .then((response) => setOrganization(response.data))
      // Still allow ordering without the prefill; the backend refuses a repeat order.
      .catch(() => setOrganization({}));
  }, [organizationId]);

  const orderRequestedAt = organization?.terminal_contact_requested_at;

  const handleOrderSent = (payload) => {
    setOrderDialogOpen(false);
    setOrganization((previous) => ({
      ...previous,
      terminal_contact_requested_at: payload?.requested_at || new Date().toISOString(),
    }));
    setSnackbar({ open: true, message: t("terminal.order.sent"), severity: "success" });
  };

  const fetchTerminals = async () => {
    try {
      const response = await axios.get("/api/payment-terminals/");
      setTerminals(response.data.results || response.data);
    } catch {
      setSnackbar({ open: true, message: t("Error"), severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setEditingTerminal(null);
    setFormData({ terminal_id: "", name: "", api_username: "", api_password: "" });
    setDialogOpen(true);
  };

  const handleOpenEdit = (terminal) => {
    setEditingTerminal(terminal);
    setFormData({
      terminal_id: terminal.terminal_id,
      name: terminal.name || "",
      api_username: "",
      api_password: "",
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingTerminal) {
        // Only send credentials if user entered new ones (strip empty strings)
        const payload = {
          terminal_id: formData.terminal_id,
          name: formData.name,
        };
        if (formData.api_username) payload.api_username = formData.api_username;
        if (formData.api_password) payload.api_password = formData.api_password;
        await axios.patch(`/api/payment-terminals/${editingTerminal.id}/`, payload);
        setSnackbar({ open: true, message: t("terminal.updated"), severity: "success" });
      } else {
        await axios.post("/api/payment-terminals/", formData);
        setSnackbar({ open: true, message: t("terminal.added"), severity: "success" });
        // The first terminal switches payment_terminal_enabled on for the org.
        fetchUser();
      }
      setDialogOpen(false);
      fetchTerminals();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t("Error"),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingTerminal) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/payment-terminals/${deletingTerminal.id}/`);
      setSnackbar({ open: true, message: t("terminal.deleted"), severity: "success" });
      setDeleteDialogOpen(false);
      setDeletingTerminal(null);
      fetchTerminals();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.response?.data?.detail || t("Error"),
        severity: "error",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleTestConnection = async (terminal) => {
    setTesting(terminal.id);
    try {
      await axios.post(`/api/payment-terminals/${terminal.id}/test-connection/`);
      setSnackbar({ open: true, message: t("terminal.connectionOk"), severity: "success" });
    } catch (err) {
      setSnackbar({
        open: true,
        message: `${t("terminal.connectionFailed")}: ${err.response?.data?.message || ""}`,
        severity: "error",
      });
    } finally {
      setTesting(null);
    }
  };

  const handleClearUnconfirmed = async () => {
    if (!clearingTerminal) return;
    setClearing(clearingTerminal.id);
    try {
      const response = await axios.post(
        `/api/payment-terminals/${clearingTerminal.id}/clear-unconfirmed/`
      );
      const { cleared_nexi_count, cleared_db_count } = response.data;
      const total = (cleared_nexi_count || 0) + (cleared_db_count || 0);
      setSnackbar({
        open: true,
        message: total > 0
          ? t("terminal.clearedTransactions", { count: total })
          : t("terminal.noStuckTransactions"),
        severity: "success",
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || t("Error"),
        severity: "error",
      });
    } finally {
      setClearing(null);
      setClearDialogOpen(false);
      setClearingTerminal(null);
    }
  };

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: "info.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "white",
              }}
            >
              <PointOfSaleIcon />
            </Box>
            <Box sx={{ ml: 2, flex: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {t("terminal.title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("terminal.description")}
              </Typography>
            </Box>
          </Box>

          {loading ? (
            <CircularProgress size={24} />
          ) : terminals.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t("terminal.noTerminals")}
            </Typography>
          ) : (
            <List dense disablePadding>
              {terminals.map((terminal) => (
                <ListItem key={terminal.id} disableGutters sx={{ px: 0 }}>
                  <ListItemText
                    primary={terminal.name || terminal.terminal_id}
                    secondary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary">
                          {terminal.terminal_id}
                        </Typography>
                        <Chip
                          size="small"
                          label={terminal.is_active ? t("terminal.active") : t("terminal.inactive")}
                          color={terminal.is_active ? "success" : "default"}
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setClearingTerminal(terminal);
                        setClearDialogOpen(true);
                      }}
                      disabled={clearing === terminal.id}
                      title={t("terminal.clearUnconfirmed")}
                    >
                      {clearing === terminal.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <RefreshIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleTestConnection(terminal)}
                      disabled={testing === terminal.id}
                      title={t("terminal.testConnection")}
                    >
                      {testing === terminal.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <CheckCircleIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenEdit(terminal)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingTerminal(terminal);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}

          <Button
            startIcon={<AddIcon />}
            size="small"
            onClick={handleOpenAdd}
            sx={{ mt: 1 }}
          >
            {t("terminal.addTerminal")}
          </Button>

          {!loading && terminals.length === 0 && (
            <Box
              sx={{ borderTop: "1px solid", borderColor: "divider", mt: 2, pt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}
              data-testid="terminal-order-section"
            >
              {orderRequestedAt ? (
                <Alert severity="success" sx={{ fontSize: "13px" }} data-testid="terminal-order-requested">
                  {t("terminal.order.requestedAt", {
                    date: new Date(orderRequestedAt).toLocaleDateString("fi-FI"),
                  })}
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary">
                    {t("terminal.order.intro")}
                  </Typography>
                  <TerminalPriceList />
                  {canOrder && (
                    <Box>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<ShoppingCartIcon />}
                        onClick={() => setOrderDialogOpen(true)}
                        disabled={!organization}
                        data-testid="terminal-order-button"
                      >
                        {t("terminal.order.button")}
                      </Button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTerminal ? t("terminal.editTerminal") : t("terminal.addTerminal")}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label={t("terminal.terminalId")}
              value={formData.terminal_id}
              onChange={(e) => setFormData({ ...formData, terminal_id: e.target.value })}
              helperText={t("terminal.terminalIdHelp")}
              required
              fullWidth
              size="small"
            />
            <TextField
              label={t("terminal.terminalName")}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              helperText={t("terminal.terminalNameHelp")}
              fullWidth
              size="small"
            />
            <TextField
              label={t("terminal.apiUsername")}
              value={formData.api_username}
              onChange={(e) => setFormData({ ...formData, api_username: e.target.value })}
              placeholder={editingTerminal ? t("Leave empty to keep current") : "npi_..."}
              required={!editingTerminal}
              fullWidth
              size="small"
            />
            <TextField
              label={t("terminal.apiPassword")}
              value={formData.api_password}
              onChange={(e) => setFormData({ ...formData, api_password: e.target.value })}
              placeholder={editingTerminal ? t("Leave empty to keep current") : "nps_..."}
              type="password"
              required={!editingTerminal}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !formData.terminal_id || (!editingTerminal && (!formData.api_username || !formData.api_password))}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      <TerminalOrderDialog
        open={orderDialogOpen}
        onClose={() => setOrderDialogOpen(false)}
        onSent={handleOrderSent}
        organization={organization}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>{t("terminal.deleteTerminal")}</DialogTitle>
        <DialogContent>
          <Typography>{t("terminal.deleteConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>{t("Cancel")}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : null}
          >
            {t("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear Unconfirmed Dialog */}
      <Dialog open={clearDialogOpen} onClose={() => !clearing && setClearDialogOpen(false)}>
        <DialogTitle>{t("terminal.clearUnconfirmedTitle")}</DialogTitle>
        <DialogContent>
          <Typography>{t("terminal.clearUnconfirmedConfirm")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearDialogOpen(false)} disabled={!!clearing}>
            {t("Cancel")}
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleClearUnconfirmed}
            disabled={!!clearing}
            startIcon={clearing ? <CircularProgress size={16} /> : null}
          >
            {t("terminal.clearUnconfirmed")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PaymentTerminalCard;
